import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../pages-styles/project-consultancy.css';
import { usePageMeta } from '../../hooks/usePageMeta';

export default function ClientResponsibilities() {
  usePageMeta(
    'Client Responsibilities - Magbity Agency Ltd',
    'Understanding client obligations and roles in successful project delivery and collaboration.'
  );

  useEffect(() => {
    const el = document.querySelector('.resp-3d-card');
    if (el) el.classList.add('enter');
  }, []);

  return (
    <div className="project-consultancy">
      <section className="consultancy-hero-banner resp-hero">
        <div className="consultancy-hero-overlay"></div>
        <div className="consultancy-hero-content">
          <div className="consultancy-hero-text">
            <h1>Client Responsibilities & Partnership</h1>
            <p>
              Successful project delivery requires active participation and responsibility from both parties. 
              Understanding client obligations ensures clear expectations, effective collaboration, and optimal project outcomes.
            </p>
          </div>

          <div className="resp-illustration">
            <div className="resp-3d-scene">
              <div className="resp-3d-card">
                <div className="face front">
                  <svg width="220" height="220" viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="respGrad" x1="0" x2="1">
                        <stop offset="0%" stopColor="#16c784" />
                        <stop offset="100%" stopColor="#00d4ff" />
                      </linearGradient>
                    </defs>
                    <rect x="10" y="10" width="200" height="200" rx="16" fill="url(#respGrad)" opacity="0.12" />
                    <g transform="translate(40,40) scale(0.9)">
                      <circle cx="30" cy="25" r="12" fill="#fff" opacity="0.14" />
                      <rect x="10" y="50" width="40" height="8" rx="3" fill="#fff" opacity="0.12" />
                      <rect x="10" y="65" width="35" height="6" rx="2" fill="#fff" opacity="0.1" />
                      <path d="M30 85 L20 95 L40 95 Z" fill="#fff" opacity="0.12" />
                    </g>
                  </svg>
                  <div className="card-label">Partnership</div>
                </div>
                <div className="face back">
                  <h4>Mutual Success</h4>
                  <p>Active collaboration and shared responsibility for project success.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="consultancy-services resp-details">
        <h2>Key Client Responsibilities</h2>
        <div className="services-list">
          <div className="service-item left">
            <div className="service-content">
              <h3>Timely Information & Documentation Provision</h3>
              <p>
                Clients must provide accurate, complete, and timely information required for project planning 
                and execution. This includes business requirements, data, approvals, and necessary documentation.
              </p>
              <ul className="service-details-list">
                <li>Provide clear project requirements and objectives</li>
                <li>Supply necessary data, assets, and documentation</li>
                <li>Establish clear decision-making authority</li>
                <li>Communicate organizational structure and contacts</li>
                <li>Provide timely responses to information requests</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM16 18H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L19.5 9H13z" opacity=".2"/>
                <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM16 18H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L19.5 9H13z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z" opacity=".2"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Designated Points of Contact & Authority</h3>
              <p>
                Establish clear communication channels with designated decision-makers who have the authority 
                to provide approvals, clarifications, and direction throughout the project lifecycle.
              </p>
              <ul className="service-details-list">
                <li>Designate project stakeholder representatives</li>
                <li>Define approval authority and escalation paths</li>
                <li>Establish communication protocols and frequency</li>
                <li>Provide rapid response to consultant inquiries</li>
                <li>Ensure continuity of contact availability</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>Timely Feedback & Decision Making</h3>
              <p>
                Provide prompt feedback on deliverables and make timely decisions on recommendations. 
                Delays in client feedback directly impact project timelines and outcomes.
              </p>
              <ul className="service-details-list">
                <li>Review and provide feedback within agreed timelines</li>
                <li>Make decisions on proposed recommendations promptly</li>
                <li>Resolve internal conflicts to support project progress</li>
                <li>Schedule and attend meetings as planned</li>
                <li>Approve changes and scope adjustments in writing</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" opacity=".2"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" opacity=".2"/>
                <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Resource Availability & Support</h3>
              <p>
                Ensure adequate resources, access to systems/data, and organizational support for project 
                implementation and adoption. This includes staff availability and budget allocation.
              </p>
              <ul className="service-details-list">
                <li>Provide necessary hardware, software, and infrastructure</li>
                <li>Allocate staff time for training and transition</li>
                <li>Ensure budget availability for project activities</li>
                <li>Grant system access and security permissions</li>
                <li>Support change management and organizational adoption</li>
              </ul>
            </div>
          </div>

          <div className="service-item left">
            <div className="service-content">
              <h3>Compliance & Internal Alignment</h3>
              <p>
                Ensure organizational compliance with policies, procedures, and government regulations. 
                Align internal stakeholders on project objectives and secure necessary approvals.
              </p>
              <ul className="service-details-list">
                <li>Ensure legal and regulatory compliance</li>
                <li>Secure internal executive and board approvals</li>
                <li>Align departments on project objectives</li>
                <li>Provide necessary documentation and licenses</li>
                <li>Manage internal stakeholder communication</li>
              </ul>
            </div>
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 7h10v2H7zm0 4h10v2H7zm0 4h7v2H7z" opacity=".2"/>
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 7h10v2H7zm0 4h10v2H7zm0 4h7v2H7z"/>
              </svg>
            </div>
          </div>

          <div className="service-item right">
            <div className="service-icon-wrapper">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="#16c784" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" opacity=".2"/>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            </div>
            <div className="service-content">
              <h3>Implementation & Change Management</h3>
              <p>
                Actively support implementation activities, manage organizational change, and drive adoption 
                of new solutions. Success requires client commitment and staff engagement.
              </p>
              <ul className="service-details-list">
                <li>Participate actively in implementation activities</li>
                <li>Communicate changes to all affected staff</li>
                <li>Provide necessary training and support</li>
                <li>Resolve staff concerns and resistance</li>
                <li>Monitor adoption and report on results</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="responsibility-checklist">
        <h2>Pre-Project Checklist</h2>
        <div className="checklist-grid">
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Clear Project Scope</h4>
            <p>Well-defined objectives, deliverables, and success criteria</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Identified Stakeholders</h4>
            <p>All key stakeholders identified with defined roles</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Budget Allocation</h4>
            <p>Adequate funding available for all project activities</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Resource Availability</h4>
            <p>Staff and resources committed for implementation</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>System Access</h4>
            <p>Access granted to necessary systems and data</p>
          </div>
          <div className="checklist-item">
            <div className="checklist-icon">✓</div>
            <h4>Executive Support</h4>
            <p>Executive sponsorship and organizational commitment</p>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <h2>Ready to Establish a Strong Partnership?</h2>
        <p>Let's align on responsibilities and set your project up for success.</p>
        <Link to="/contact" className="cta-button">Initiate Project Partnership</Link>
      </section>
    </div>
  );
}
